import { useEffect, useState } from 'react';
import { NewAssetModal } from './NewAssetModal';
import { KOKORO_VOICES } from '../zerovoice/voiceMapping';
import { playAudio } from '../hooks/useAudioPlayer';
import { synthesizeBlendLocal } from '../zerovoice/speakKokoroBlend';
import type { VoiceSpec } from '../zerovoice/types';

interface Props {
  open: boolean;
  onClose: () => void;
  agentName: string;
  worldSeedHex: string;
  /** Current spec to prefill. If null, open with a reasonable default. */
  initialSpec: VoiceSpec | null;
  onSave: (spec: VoiceSpec) => Promise<void>;
}

const DEFAULT_SPEC: VoiceSpec = {
  voiceA: 0,
  voiceB: 10,
  t: 0.5,
  pitch: 1.0,
  energy: 1.0,
};

function fmt(n: number, digits = 2): string {
  return n.toFixed(digits);
}

/**
 * Stage 12 (E3): manual-override dialog for an agent's VoiceSpec. Lets
 * the user pick voiceA, voiceB, t, pitch, energy directly and save them
 * into `kokoro_agent_pins.spec_json` (same shape the Stage 8 hash-derived
 * pins already use). Preview plays the current spec locally.
 */
export function CustomizeSpecDialog({
  open,
  onClose,
  agentName,
  worldSeedHex,
  initialSpec,
  onSave,
}: Props) {
  const [spec, setSpec] = useState<VoiceSpec>(initialSpec ?? DEFAULT_SPEC);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSpec(initialSpec ?? DEFAULT_SPEC);
      setErr(null);
    }
  }, [open, initialSpec]);

  const set = <K extends keyof VoiceSpec>(key: K, value: VoiceSpec[K]): void => {
    setSpec((prev) => ({ ...prev, [key]: value }));
  };

  const preview = async (): Promise<void> => {
    setPreviewing(true);
    setErr(null);
    let url: string | null = null;
    try {
      const { wav } = await synthesizeBlendLocal({
        text: `Hello, I am ${agentName || 'this agent'}.`,
        spec,
        worldSeedHex,
      });
      url = URL.createObjectURL(wav);
      await playAudio(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (url) URL.revokeObjectURL(url);
      setPreviewing(false);
    }
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await onSave(spec);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={() => void preview()}
        disabled={previewing || busy}
      >
        {previewing ? 'Playing…' : '▶ Preview'}
      </button>
      <div style={{ flex: 1 }} />
      <button type="button" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={() => void save()}
        disabled={busy || spec.voiceA === spec.voiceB}
      >
        {busy ? 'Saving…' : 'Save & pin'}
      </button>
    </>
  );

  return (
    <NewAssetModal
      open={open}
      onClose={onClose}
      busy={busy}
      title={`Customize voice — ${agentName}`}
      footer={footer}
    >
      <div className="stack">
        <div className="row">
          <label className="stack" style={{ flex: 1 }}>
            <span className="muted small">Voice A</span>
            <select
              value={spec.voiceA}
              onChange={(e) => set('voiceA', Number(e.target.value))}
            >
              {KOKORO_VOICES.map((v, i) => (
                <option key={v} value={i}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="stack" style={{ flex: 1 }}>
            <span className="muted small">Voice B</span>
            <select
              value={spec.voiceB}
              onChange={(e) => set('voiceB', Number(e.target.value))}
            >
              {KOKORO_VOICES.map((v, i) => (
                <option key={v} value={i}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        </div>
        {spec.voiceA === spec.voiceB ? (
          <div className="muted small">
            Voice A and B are the same — blend degenerates to a single voice.
            Pick two different voices for a true blend.
          </div>
        ) : null}
        <label className="stack">
          <span className="muted small">
            Blend t = {fmt(spec.t, 3)} (0 = pure A, 1 = pure B)
          </span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={spec.t}
            onChange={(e) => set('t', Number(e.target.value))}
          />
        </label>
        <label className="stack">
          <span className="muted small">
            Pitch = {fmt(spec.pitch)} (1.0 = natural)
          </span>
          <input
            type="range"
            min={0.85}
            max={1.15}
            step={0.01}
            value={spec.pitch}
            onChange={(e) => set('pitch', Number(e.target.value))}
          />
        </label>
        <label className="stack">
          <span className="muted small">
            Energy = {fmt(spec.energy)} (1.0 = natural)
          </span>
          <input
            type="range"
            min={0.8}
            max={1.2}
            step={0.01}
            value={spec.energy}
            onChange={(e) => set('energy', Number(e.target.value))}
          />
        </label>
        {err ? <div className="status-missing small">{err}</div> : null}
      </div>
    </NewAssetModal>
  );
}
